import {AdvancedSearchType} from 'src/openapi/admin/models/AdvancedSearchType';
import {AlertFrequencyType} from 'src/openapi/admin/models/AlertFrequencyType';
import {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import {GeneralAlertType} from 'src/openapi/admin/models/GeneralAlertType';
import {SavedListType} from 'src/openapi/admin/models/SavedListType';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import {TreeItem, type TreeStructure} from '@pb/kit-component-tree-v2';

import {LIST_TYPES, SEARCH_TYPES, SORT_COLUMNS} from '../constants';
import {FilterableType, SavedSearchType} from '../typedef';


export const uiToApiSort = (d: 'ASC' | 'DESC'): SortDirection =>
    d === 'ASC' ? SortDirection.ASC : SortDirection.DESC;

export const apiToUiSort = (d: SortDirection | undefined): 'ASC' | 'DESC' | undefined => {
    if (d === SortDirection.ASC) {
        return 'ASC';
    } else if (d === SortDirection.DESC) {
        return 'DESC';
    } else {
        return undefined;
    }
};

const mapTree = (data: DictionaryTree, disabled?: boolean): TreeItem => {
    return {
        id: data.id,
        caption: data.description,
        disabled,
        expanded: true,
        ...data.children?.length
            ? ({children: data.children?.map(childData => mapTree(childData, disabled))})
            : null,
    };
};

export const getTreeData = (availableTree: DictionaryTree, disable?: boolean): Array<TreeItem> => {
    if (!availableTree) {
        return [];
    }
    const treeStructure = mapTree(availableTree, disable)?.children as TreeStructure;
    return treeStructure || [];
};

export const getGeneralAlertItemType = (generalAlertType: GeneralAlertType): 'Saved Search' | 'Saved List' => {
    switch (generalAlertType) {
        case GeneralAlertType.SAVED_SEARCH:
            return 'Saved Search';
        case GeneralAlertType.SAVED_LIST:
            return 'Saved List';
        default:
            return generalAlertType;
    }
};

export const descriptionMap: Partial<Record<AlertFrequencyType, string>> = {
    [AlertFrequencyType.DAILY]: 'Emails sent daily at 11:00 AM UTC',
    [AlertFrequencyType.WEEKLY]: 'Emails sent every Monday at 11:00 AM UTC',
    [AlertFrequencyType.MONTHLY]: 'Emails sent every first Monday of the month at 11:00 AM UTC',
};

export const ERROR_MESSAGES = {
    LOAD_ALERT_OPTIONS: 'Failed to load alert options. Please try again.',
    LOAD_CONTENT_ALERT: 'Failed to load content alert. Please try again.',
    LOAD_SEARCH_OPTIONS: 'Failed to load search options. Please try again.',
    LOAD_LIST_OPTIONS: 'Failed to load list options. Please try again.',
} as const;

export const ALERT_TYPE_MAPPING = {
    SP: 'SERVICE_PROVIDER',
    LP: 'LIMITED_PARTNER',
} as const;

const formatLabel = (value: string) => value.split('_').map(w => w[0] + w.slice(1).toLowerCase()).join(' ');

export const getTypeCaption = (code: string) => {
    return SEARCH_TYPES[code] ?? LIST_TYPES[code] ?? formatLabel(code);
};

export const getSavedType = (type: SavedSearchType): string => {
    return type === 'SEARCH' ? 'Search' : 'List';
};

export const getTypeOptions = (savedSearchType: SavedSearchType): FilterableType[] => {
    return savedSearchType === 'SEARCH'
        ? Object.values(AdvancedSearchType)
        : Object.values(SavedListType);
};

export const buildSelectedValues = (selectedTypeValue: FilterableType | FilterableType[] | undefined): string[] => {
    if (Array.isArray(selectedTypeValue)) {
        return (selectedTypeValue as FilterableType[]).map(v => String(v));
    }
    if (selectedTypeValue) {
        return [String(selectedTypeValue as FilterableType)];
    }
    return [];
};

export type SortColumn = typeof SORT_COLUMNS[keyof typeof SORT_COLUMNS];

export const getDirection = <T>(
    column: SortColumn,
    lastSortedColumn: string | null | undefined,
    directions: Record<SortColumn, T | null | undefined>,
): T | null => {
    if (lastSortedColumn !== column) {
        return null;
    }

    return directions[column] ?? null;
};


